import React, { useState } from 'react'
import styles from '../../UpdateSiteModal.module.css'
import { Button } from 'react-bootstrap'
import { Field, ErrorMessage } from 'formik'
import { showUpgradeCardModal } from 'actions'
import { useDispatch } from 'react-redux'

const FormInput = ({ label, name, placeholder, id, maxLength, disabled, showPaywall }) => {
  const inputId = id || `input-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <div className={styles.formGroup}>
      <div className={styles.optionDetails} id={inputId} style={{ marginBottom: '8px' }}>
        <div className={styles.optionTitle}>{label}</div>
        <div className={styles.optionDescription}>
          {maxLength && <span className={styles.maxLengthLabel}>Recommended: Max. {maxLength} characters</span>}
        </div>
      </div>
      <Field name={name}>
        {({ field, form }) => {
          const error = form.errors[name]
          const touched = form.touched[name]
          const currentLength = field.value ? field.value.length : 0

          const handleClick = (e) => {
            e.preventDefault()
            showPaywall()
          }

          return (
            <>
              <div onClick={handleClick}>
                <input
                  type="text"
                  id={inputId}
                  className={`${styles.formControl} ${touched && error ? styles.inputError : ''}`}
                  placeholder={placeholder}
                  aria-label={label}
                  maxLength={maxLength}
                  readOnly={disabled}
                  {...field}
                />
              </div>
              {maxLength && (
                <div
                  style={{
                    textAlign: 'right',
                    fontSize: '12px',
                    color: currentLength > maxLength ? '#dc3545' : '#888',
                    marginTop: '4px'
                  }}
                >
                  {currentLength}/{maxLength}
                </div>
              )}
              {touched && error && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{error}</div>}
            </>
          )
        }}
      </Field>
    </div>
  )
}

const TextArea = ({ label, name, placeholder, id, maxLength, disabled, showPaywall }) => {
  const inputId = id || `textarea-${label.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <div className={styles.formGroup}>
      <div className={styles.optionDetails} id={inputId} style={{ marginBottom: '8px' }}>
        <div className={styles.optionTitle}>{label}</div>
        <div className={styles.optionDescription}>
          {maxLength && <span className={styles.maxLengthLabel}>Recommended: Max. {maxLength} characters</span>}
        </div>
      </div>
      <Field name={name}>
        {({ field, form }) => {
          const error = form.errors[name]
          const touched = form.touched[name]
          const currentLength = field.value ? field.value.length : 0

          const handleClick = (e) => {
            e.preventDefault()
            showPaywall()
          }

          return (
            <>
              <div onClick={handleClick}>
                <textarea
                  id={inputId}
                  className={`${styles.formControl} ${touched && error ? styles.inputError : ''}`}
                  placeholder={placeholder}
                  aria-label={label}
                  maxLength={maxLength}
                  rows={4}
                  style={{ resize: 'none' }}
                  readOnly={disabled}
                  {...field}
                />
              </div>
              {maxLength && (
                <div
                  style={{
                    textAlign: 'right',
                    fontSize: '12px',
                    color: currentLength > maxLength ? '#dc3545' : '#888',
                    marginTop: '4px'
                  }}
                >
                  {currentLength}/{maxLength}
                </div>
              )}
              {touched && error && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{error}</div>}
            </>
          )
        }}
      </Field>
    </div>
  )
}

const ImageUpload = ({
  label,
  subText,
  id,
  name,
  disabled,
  showPaywall,
  maxSizeMB = 5,
  maxDimensions = null,
  minDimensions = null
}) => {
  const inputId = id || `file-${label.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <div className={styles.formGroup}>
      <div className={styles.optionDetails} style={{ marginBottom: '8px' }}>
        <div className={styles.optionTitle}>{label}</div>
        <div className={styles.optionDescription}>{subText}</div>
      </div>

      <Field name={name}>
        {({ field, form }) => {
          const [parent, child] = name.split('.')
          const error = parent && child ? form.errors[parent]?.[child] : form.errors[name]
          const { value } = field
          const handleFileChange = (e) => {
            const file = e.target.files[0]
            const inputElement = e.target
            if (file) {
              // Check file size
              if (file.size > maxSizeMB * 1024 * 1024) {
                form.setFieldError(name, `Your file is above the ${maxSizeMB}MB limit`)
                inputElement.value = ''
                return
              }

              // Check image dimensions if specified
              if (maxDimensions || minDimensions) {
                const img = new Image()
                img.onload = () => {
                  if (maxDimensions && (img.width > maxDimensions.width || img.height > maxDimensions.height)) {
                    form.setFieldError(
                      name,
                      `Image dimensions must be ${maxDimensions.width}x${maxDimensions.height} or smaller`
                    )
                    inputElement.value = ''
                    URL.revokeObjectURL(img.src)
                    return
                  }
                  if (minDimensions && (img.width < minDimensions.width || img.height < minDimensions.height)) {
                    form.setFieldError(
                      name,
                      `Image must be at least ${minDimensions.width}x${minDimensions.height} pixels`
                    )
                    inputElement.value = ''
                    URL.revokeObjectURL(img.src)
                    return
                  }
                  form.setFieldValue(name, file)
                  URL.revokeObjectURL(img.src)
                }
                img.onerror = () => {
                  form.setFieldError(name, 'Invalid image file')
                  inputElement.value = ''
                  URL.revokeObjectURL(img.src)
                }
                img.src = URL.createObjectURL(file)
                return
              }

              form.setFieldValue(name, file)
            }
          }

          const handleDeleteImage = () => {
            form.setFieldValue(name, null)
          }

          const handleContainerClick = (e) => {
            if (disabled) {
              e.preventDefault()
              if (showPaywall) {
                showPaywall()
              }
              return
            }
            document.getElementById(inputId).click()
          }

          return (
            <>
              {error && <div style={{ color: '#dc3545', fontSize: '12px', marginBottom: '8px' }}>{error}</div>}

              {!value ? (
                <div
                  className={`${styles.imageUpload} ${disabled ? styles.disabledUpload : ''}`}
                  onClick={handleContainerClick}
                  style={{ cursor: 'pointer' }}
                  data-testid="SEOSettingsSection-og-image-upload-container"
                >
                  <input
                    type="file"
                    id={inputId}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    disabled={disabled}
                    name={name}
                    data-testid="SEOSettingsSection-input-add-og-image"
                  />
                  <div style={{ marginBottom: '8px' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12 5V19M5 12H19"
                        stroke="#666"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>Click to upload your image</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                    Maximum file size {maxSizeMB}mb
                  </div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    Supported formats: <span style={{ fontWeight: 'bold' }}> .jpg, .jpeg, .png, .gif, .webp</span>
                  </div>
                </div>
              ) : (
                <div className={styles.imagePreviewContainer}>
                  <div className={styles.imagePreview}>
                    <img
                      src={typeof value === 'string' ? value : URL.createObjectURL(value)}
                      alt="OG Preview"
                      style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }}
                    />
                  </div>
                  <div className={styles.imagePreviewFooter} data-testid="SEOSettingsSection-img-preview-footer">
                    <div className={styles.fileName}>
                      {typeof value === 'string' ? value.split('/').pop() : value.name}
                    </div>
                    <button
                      type="button"
                      className={styles.deleteImageBtn}
                      onClick={disabled ? showPaywall : handleDeleteImage}
                      disabled={disabled}
                      data-testid="SEOSettingsSection-img-preview-delete-btn"
                    >
                      <img src="/assets/icons/trash.svg" alt="delete" width="20" height="20" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Field>
    </div>
  )
}

const SEOSettingsSection = ({ setFieldValue, userProfile, uploadValues, site }) => {
  const dispatch = useDispatch()
  const isTrial = !userProfile?.productId

  const showPaywall = () => {
    if (isTrial) {
      dispatch(
        showUpgradeCardModal({
          title: 'Advanced settings for your project',
          desc: "Customize your project's every setting by upgrading to our paid plans",
          cta: 'Upgrade Now'
        })
      )
      return true
    }
    return false
  }

  if (!uploadValues?.selectedFile && !site?.isTransferRequestLink) {
    return null
  }

  return (
    <div id="advanced_settings" className={styles.section}>
      <span className={styles.sectionTitle}>
        <img src="/assets/icons/settings-filled.svg" alt="advanced_settings" height="24" width="24" />
        <h3 className="mb-0 font-18">Advanced Settings</h3>
      </span>

      <div className={styles.advancedSettings}>
        <FormInput
          label="Page Title"
          name="toggleValues.pageTitle"
          placeholder="Enter text here"
          id="page-title"
          maxLength={60}
          disabled={isTrial}
          showPaywall={showPaywall}
        />

        <TextArea
          label="Meta description"
          name="toggleValues.metaDescription"
          placeholder="Enter text here"
          id="meta-description"
          maxLength={145}
          disabled={isTrial}
          showPaywall={showPaywall}
        />

        <ImageUpload
          label="Preview Image"
          subText="Minimum 200x200px, ideally 1200x630px (2:1 ratio). Less than 300KB"
          id="og-image"
          name="toggleValues.ogImage"
          disabled={isTrial}
          showPaywall={showPaywall}
          maxSizeMB={0.3}
          minDimensions={{ width: 200, height: 200 }}
        />

        <ImageUpload
          label="Logo"
          subText="Maximum size: 64x64 pixels"
          id="logoImage"
          name="toggleValues.logoImage"
          disabled={isTrial}
          showPaywall={showPaywall}
          maxSizeMB={5}
          maxDimensions={{ width: 64, height: 64 }}
        />
      </div>
    </div>
  )
}

export default SEOSettingsSection
